import * as v from 'valibot';

/**
 * The software factory's shared vocabulary.
 *
 * Every stage of the factory — triage, code generation, validation, release,
 * documentation, monitoring — hands its output to the next one. The failure
 * mode a factory has that a single agent does not is the *bad handoff*: a step
 * that produced almost-right data, and five steps downstream nobody can tell
 * whether the answer is wrong because the model was wrong or because the shape
 * was. So the handoff is typed, once, here, and every stage validates at its
 * own boundary rather than trusting the stage before it.
 *
 * One work item is threaded through the whole loop. It gains fields as it goes
 * — a PR when code generation finishes, a verdict when validation finishes, a
 * version when release finishes — and it never loses the `signal` it came from,
 * because "why does this change exist" is the question a human asks first and
 * the one an audit trail most often cannot answer.
 */

/** Where a piece of work came from. */
export const SignalSource = v.picklist([
  'github_issue',
  'github_pr',
  'sentry',
  'deployment',
  'monitoring',
  'manual',
]);
export type SignalSource = v.InferOutput<typeof SignalSource>;

/**
 * P0 to P3, and nothing finer.
 *
 * Four levels because a triage agent asked for a 1–10 score produces a 7 for
 * everything. Four forces a decision that means something: P0 wakes somebody,
 * P3 waits for a quiet week.
 */
export const Priority = v.picklist(['P0', 'P1', 'P2', 'P3']);
export type Priority = v.InferOutput<typeof Priority>;

export const WorkItemKind = v.picklist([
  'bug',
  'feature',
  'incident',
  'documentation',
  'chore',
  'security',
]);
export type WorkItemKind = v.InferOutput<typeof WorkItemKind>;

export const WorkItemState = v.picklist([
  'triaged',
  'in_progress',
  'in_validation',
  'awaiting_release',
  'released',
  'documented',
  'escalated',
  'rejected',
]);
export type WorkItemState = v.InferOutput<typeof WorkItemState>;

/**
 * The signal as it arrived, with enough context attached that no agent has to
 * go hunting for it.
 *
 * `context` is deliberately free-form and deliberately required to be *full*:
 * an incoming Sentry signal carries the stack trace, the breadcrumbs, the
 * affected user count and first-seen/last-seen, not just an issue id. Context
 * quality at the input decides decision quality everywhere downstream, and a
 * step that has to fetch the trace itself is a step that sometimes does not.
 */
export const Signal = v.object({
  source: SignalSource,
  /** Provider-native id: issue number, Sentry issue id, deployment sha. */
  externalId: v.string(),
  url: v.optional(v.nullable(v.string())),
  title: v.string(),
  body: v.optional(v.nullable(v.string())),
  repo: v.optional(v.nullable(v.string())),
  labels: v.optional(v.array(v.string()), []),
  observedAt: v.string(),
  /** Everything else the source knew, verbatim. */
  context: v.optional(v.record(v.string(), v.unknown()), {}),
});
export type Signal = v.InferOutput<typeof Signal>;

/** What a reviewer sees on a validation verdict, per dimension. */
export const ValidationDimension = v.picklist([
  'correctness',
  'security',
  'tests',
  'performance',
]);
export type ValidationDimension = v.InferOutput<typeof ValidationDimension>;

export const DimensionVerdict = v.object({
  dimension: ValidationDimension,
  /** `pass`, `concerns` and `fail` — three, because a binary hides the middle. */
  verdict: v.picklist(['pass', 'concerns', 'fail']),
  /** 0–100, and only meaningful next to the verdict it explains. */
  confidence: v.pipe(v.number(), v.minValue(0), v.maxValue(100)),
  findings: v.optional(v.array(v.string()), []),
  notes: v.optional(v.string(), ''),
});
export type DimensionVerdict = v.InferOutput<typeof DimensionVerdict>;

/**
 * The gate. Four dimensions, each scored on its own.
 *
 * Scored separately because a single "looks good" is unevaluable: a change that
 * is correct and insecure and one that is secure and wrong both come out as
 * "mostly fine", and the two need opposite responses.
 */
export const ValidationVerdict = v.object({
  workItemId: v.string(),
  pullRequest: v.nullable(v.number()),
  dimensions: v.array(DimensionVerdict),
  /** Derived, not asserted: any `fail` fails the gate. */
  approved: v.boolean(),
  /** Set when the gate wants a person rather than a retry. */
  escalationReason: v.optional(v.nullable(v.string())),
  reviewedAt: v.string(),
});
export type ValidationVerdict = v.InferOutput<typeof ValidationVerdict>;

/** The pull request a code-generation run produced. */
export const ProposedChange = v.object({
  repo: v.string(),
  branch: v.string(),
  pullRequest: v.nullable(v.number()),
  url: v.optional(v.nullable(v.string())),
  filesTouched: v.optional(v.array(v.string()), []),
  testsAdded: v.optional(v.array(v.string()), []),
  /** The flag new behaviour ships behind, when there is one. */
  flagKey: v.optional(v.nullable(v.string())),
  summary: v.string(),
});
export type ProposedChange = v.InferOutput<typeof ProposedChange>;

export const ReleaseRecord = v.object({
  version: v.string(),
  changelog: v.string(),
  deployedAt: v.nullable(v.string()),
  healthy: v.nullable(v.boolean()),
  rolledBack: v.optional(v.boolean(), false),
  rollbackReason: v.optional(v.nullable(v.string())),
});
export type ReleaseRecord = v.InferOutput<typeof ReleaseRecord>;

/**
 * One unit of work, threaded through every stage.
 *
 * The optional tail — `change`, `verdict`, `release` — is the record of how far
 * the item got. An item with a `change` and no `verdict` is sitting in the
 * gate; one with a failing `verdict` and no `release` is the thing that stopped
 * before production, which is the whole point of having a gate.
 */
export const WorkItem = v.object({
  id: v.string(),
  kind: WorkItemKind,
  title: v.pipe(v.string(), v.minLength(1)),
  description: v.optional(v.string(), ''),
  priority: Priority,
  /** 0–100. Triage says how sure it is, and low confidence routes to a human. */
  confidence: v.optional(v.pipe(v.number(), v.minValue(0), v.maxValue(100)), 50),
  state: WorkItemState,
  repo: v.optional(v.nullable(v.string())),
  /** Which agent owns it next: `codeGen`, `documentation`, `monitoring`, `human`. */
  assignedTo: v.optional(v.nullable(v.string())),
  signal: Signal,
  /** Set when triage recognised this as a duplicate of an item it already made. */
  duplicateOf: v.optional(v.nullable(v.string())),
  change: v.optional(v.nullable(ProposedChange)),
  verdict: v.optional(v.nullable(ValidationVerdict)),
  release: v.optional(v.nullable(ReleaseRecord)),
  createdAt: v.string(),
  updatedAt: v.string(),
});
export type WorkItem = v.InferOutput<typeof WorkItem>;

/** What triage produces: the item, plus where it decided to send it. */
export const TriageResult = v.object({
  items: v.array(WorkItem),
  /** Items triage refused to create, and why — a dedupe is not a silent no-op. */
  suppressed: v.optional(
    v.array(v.object({ title: v.string(), reason: v.string() })),
    [],
  ),
  routedTo: v.optional(v.array(v.string()), []),
});
export type TriageResult = v.InferOutput<typeof TriageResult>;

// ------------------------------------------------------------------ monitoring

export const AnomalySeverity = v.picklist(['sev1', 'sev2', 'sev3', 'info']);
export type AnomalySeverity = v.InferOutput<typeof AnomalySeverity>;

export const Anomaly = v.object({
  id: v.string(),
  service: v.string(),
  severity: AnomalySeverity,
  metric: v.string(),
  /** What it is now, and what it was in the comparable window before. */
  observed: v.number(),
  baseline: v.number(),
  description: v.string(),
  firstSeen: v.string(),
});
export type Anomaly = v.InferOutput<typeof Anomaly>;

export const Incident = v.object({
  id: v.string(),
  severity: AnomalySeverity,
  title: v.string(),
  summary: v.string(),
  anomalies: v.optional(v.array(v.string()), []),
  /** The work item monitoring opened so the fix re-enters the primary loop. */
  workItemId: v.optional(v.nullable(v.string())),
  openedAt: v.string(),
  resolvedAt: v.optional(v.nullable(v.string())),
});
export type Incident = v.InferOutput<typeof Incident>;

export const HealthReport = v.object({
  windowMinutes: v.number(),
  /** 0–100, where 100 is "nothing to say". */
  healthScore: v.pipe(v.number(), v.minValue(0), v.maxValue(100)),
  anomalies: v.optional(v.array(Anomaly), []),
  incidents: v.optional(v.array(Incident), []),
  notes: v.optional(v.string(), ''),
  polledAt: v.string(),
});
export type HealthReport = v.InferOutput<typeof HealthReport>;

// --------------------------------------------------------------------- metrics

/**
 * How the factory is doing, in the three numbers that change a decision.
 *
 * Throughput without backlog flatters a factory that is falling behind;
 * backlog without cycle time cannot tell a queue that is growing from one that
 * is merely long. All three, or none.
 */
export const FactoryMetrics = v.object({
  windowHours: v.number(),
  /** Work items released in the window. */
  throughput: v.number(),
  /** Items triaged and not yet released. */
  backlog: v.number(),
  /** Median hours from triage to release, over items that finished in the window. */
  cycleTimeHours: v.nullable(v.number()),
  /** Runs that ended in `failed`, as a share of runs that ended at all. */
  failureRate: v.number(),
  /** Items sitting in `escalated` — the queue a human owns. */
  escalations: v.number(),
  byStage: v.optional(v.record(v.string(), v.number()), {}),
  computedAt: v.string(),
});
export type FactoryMetrics = v.InferOutput<typeof FactoryMetrics>;

// ---------------------------------------------------------------------- agents

/**
 * One agent as the studio lists it.
 *
 * Reef has no agent table and should not gain one: an agent here *is* its
 * instructions (a versioned skill), the connections it may reach, and the
 * workflow steps that run it. This is the projection of those three facts onto
 * one row, computed on read — so an agent cannot drift from the document that
 * defines it, because there is nothing to drift.
 */
export const FactoryAgentSummary = v.object({
  id: v.string(),
  name: v.string(),
  /** One line, from the skill's frontmatter. */
  responsibility: v.string(),
  /** The skill body — what the studio shows and what a human approves. */
  instructions: v.string(),
  skill: v.string(),
  model: v.string(),
  /** Connection mounts the agent's steps carry, e.g. `github`, `sentry`. */
  tools: v.array(v.string()),
  /** Workflow ids with a step that runs this agent. */
  workflows: v.array(v.string()),
  stepCount: v.number(),
  triggeredBy: v.string(),
  produces: v.string(),
});
export type FactoryAgentSummary = v.InferOutput<typeof FactoryAgentSummary>;
